import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { authClient } from "@/lib/authClient";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

type Status = "verifying" | "success" | "error";

export default function VerifyEmail() {
  const [, setLocation] = useLocation();
  const [status, setStatus] = useState<Status>("verifying");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const search = typeof window !== "undefined" ? window.location.search : "";
    const params = new URLSearchParams(search);
    const token = params.get("token");

    if (!token) {
      setStatus("error");
      setErrorMessage("No verification token found in this link. Please request a new one.");
      return;
    }

    authClient.verifyEmail({ query: { token } })
      .then((result) => {
        if (result.error) {
          setStatus("error");
          setErrorMessage(result.error.message ?? "Verification failed. The link may have expired.");
        } else {
          setStatus("success");
          // Short pause so the success state is visible, then redirect
          setTimeout(() => setLocation("/profile-setup"), 2200);
        }
      })
      .catch((err: any) => {
        setStatus("error");
        setErrorMessage(err?.message ?? "Verification failed. Please try again.");
      });
  }, [setLocation]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-neon/5 rounded-full blur-[150px] pointer-events-none" />

      <div className="relative w-full max-w-md text-center">
        {/* Logo */}
        <div className="flex items-center gap-2.5 justify-center mb-10">
          <div className="w-10 h-10 rounded-lg bg-neon/15 border border-neon/30 flex items-center justify-center">
            <span className="text-neon font-extrabold text-base tracking-tighter">GP</span>
          </div>
          <span className="text-2xl font-extrabold tracking-tight uppercase">
            <span className="text-white">Game</span>
            <span className="text-neon">Point</span>
          </span>
        </div>

        {/* Verifying */}
        {status === "verifying" && (
          <>
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 border-2 border-neon/30 border-t-neon rounded-full animate-spin" />
            </div>
            <h1 className="text-xl font-extrabold text-white uppercase mb-2">
              Verifying your email…
            </h1>
            <p className="text-slate-400 text-sm">Just a moment.</p>
          </>
        )}

        {/* Success */}
        {status === "success" && (
          <>
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-neon/10 border border-neon/30 flex items-center justify-center">
                <CheckCircle2 className="w-9 h-9 text-neon" />
              </div>
            </div>
            <h1 className="text-2xl font-extrabold text-white uppercase mb-3">
              Email verified!
            </h1>
            <p className="text-slate-400 text-sm mb-6">
              Your account is active. Setting up your profile now…
            </p>
            <div className="w-6 h-6 border-2 border-neon/30 border-t-neon rounded-full animate-spin mx-auto" />
          </>
        )}

        {/* Error */}
        {status === "error" && (
          <>
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-destructive/10 border border-destructive/30 flex items-center justify-center">
                <AlertCircle className="w-9 h-9 text-destructive" />
              </div>
            </div>
            <h1 className="text-2xl font-extrabold text-white uppercase mb-3">
              Verification failed
            </h1>
            <p className="text-slate-400 text-sm mb-6">{errorMessage}</p>
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => setLocation("/signup-success")}
                className="bg-neon hover:bg-neon-dim text-background font-bold uppercase"
              >
                Request a new link
              </Button>
              <button
                onClick={() => setLocation("/login")}
                className="text-xs text-slate-500 hover:text-slate-400 transition-colors"
              >
                Back to Sign In
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
